/**
 * Utility functions for generating and matching slugs from event titles
 */

/**
 * Generates a URL-friendly slug from a given title
 * 
 * @param title - The event title (e.g., "Frühlingsfest 2025")
 * @returns A URL-friendly slug (e.g., "fruehlingsfest-2025")
 * 
 * @example
 * generateSlug("Frühlingsfest 2025") // returns "fruehlingsfest-2025"
 * generateSlug("Volksfest 2025") // returns "volksfest-2025"
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD') // Normalize to decompose umlauts
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
}

/**
 * Finds a title that matches the given slug
 * 
 * @param slug - The URL slug to match (e.g., "fruehlingsfest-2025")
 * @param titles - Array of possible titles to match against
 * @returns The matching title or undefined if no match found
 * 
 * @example
 * const titles = ["Frühlingsfest 2025", "Volksfest 2025"];
 * matchSlugToTitle("fruehlingsfest-2025", titles) // returns "Frühlingsfest 2025"
 */
export function matchSlugToTitle(slug: string, titles: string[]): string | undefined {
  return titles.find(title => generateSlug(title) === slug);
}

/**
 * Validates if a slug matches a specific title
 * 
 * @param slug - The slug to validate
 * @param title - The title to match against
 * @returns true if the slug matches the title
 * 
 * @example
 * isSlugForTitle("fruehlingsfest-2025", "Frühlingsfest 2025") // returns true
 */
export function isSlugForTitle(slug: string, title: string): boolean {
  return generateSlug(title) === slug;
}

